export var SubscriptionTier;
(function (SubscriptionTier) {
    SubscriptionTier["FREE"] = "free";
    SubscriptionTier["STARTER"] = "starter";
    SubscriptionTier["ADVANCED"] = "advanced";
})(SubscriptionTier || (SubscriptionTier = {}));
export var SubscriptionStatus;
(function (SubscriptionStatus) {
    SubscriptionStatus["ACTIVE"] = "active";
    SubscriptionStatus["CANCELED"] = "canceled";
    SubscriptionStatus["PAST_DUE"] = "past_due";
    SubscriptionStatus["TRIAL"] = "trial";
    SubscriptionStatus["INCOMPLETE"] = "incomplete";
})(SubscriptionStatus || (SubscriptionStatus = {}));
// Predefined subscription plans (backend will serve the real data)
export const SUBSCRIPTION_PLANS = {
    [SubscriptionTier.FREE]: {
        id: SubscriptionTier.FREE,
        tier: SubscriptionTier.FREE,
        name: 'Free',
        description: 'Prove it works on your own applicants before committing anything. No card.',
        price: 0,
        currency: 'usd',
        billingPeriod: 'month',
        scoringCredits: 25,
        creditsRestoreMonthly: false, // one-shot grant, never replenishes
        smsLimit: 0, // SMS sends count against scoringCredits, no separate budget
        features: [
            '25 AI-scored resumes (one-time, no monthly reset)',
            'Up to 3 active jobs',
            'Send the SMS qualification flow to your own verified phone',
            'Ranked shortlist with transparent reasoning',
            'No credit card required',
        ],
        limits: {
            maxJobs: 3,
            maxCandidates: 50,
            maxTeamMembers: 1,
        },
    },
    [SubscriptionTier.STARTER]: {
        id: SubscriptionTier.STARTER,
        tier: SubscriptionTier.STARTER,
        name: 'Starter',
        description: 'For teams hiring 2–3 people a month at SMB volume.',
        price: 9900, // $99.00
        currency: 'usd',
        billingPeriod: 'month',
        scoringCredits: 1500,
        creditsRestoreMonthly: true,
        smsLimit: 300,
        features: [
            '1,500 AI scans / month',
            '300 SMS messages / month',
            'Up to 25 active jobs',
            'Up to 3 team members',
            '$0.05 per extra scan, $0.03 per extra SMS',
            'Priority email support',
        ],
        limits: {
            maxJobs: 25,
            maxTeamMembers: 3,
        },
        isPopular: true,
    },
    [SubscriptionTier.ADVANCED]: {
        id: SubscriptionTier.ADVANCED,
        tier: SubscriptionTier.ADVANCED,
        name: 'Advanced',
        description: 'For multi-location and peak-season hiring without adding a recruiter.',
        price: 24900, // $249.00
        currency: 'usd',
        billingPeriod: 'month',
        scoringCredits: 5000,
        creditsRestoreMonthly: true,
        smsLimit: 1000,
        features: [
            '5,000 AI scans / month',
            '1,000 SMS messages / month',
            'Unlimited active jobs',
            'Up to 10 team members',
            '$0.03 per extra scan, $0.02 per extra SMS',
            'API access',
            'Priority support',
        ],
        limits: {
            maxTeamMembers: 10,
        },
        isEnterprise: true,
    },
};
/**
 * `atUtc` plus `months` calendar months (UTC): the same day and time of the month, or the month's last day when
 * it's shorter (a period from Jan 31 ends Feb 28 or 29).
 */
export function addCalendarMonthsUtc(atUtc, months) {
    const date = new Date(atUtc);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + months;
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    return Date.UTC(year, month, Math.min(date.getUTCDate(), lastDay), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds());
}
/**
 * Whether the subscription's period renews here when it ends: an active plan whose credits restore each period
 * and isn't billed through Stripe (an admin assigned it). Stripe's paid renewal invoice renews Stripe plans; Free
 * never renews; a plan an admin marked anything but active (canceled, say) stops.
 */
export function renewsWithoutStripe(subscription) {
    return SUBSCRIPTION_PLANS[subscription.tier]?.creditsRestoreMonthly === true &&
        !subscription.stripeSubscriptionId &&
        subscription.status === SubscriptionStatus.ACTIVE;
}
/**
 * The period a subscription that renews here is in at `nowUtc`: calendar months on from the one that ended. The
 * same period when it hasn't ended.
 */
export function renewedPeriod(subscription, nowUtc) {
    let startUtc = subscription.currentPeriodStartUtc;
    let endUtc = subscription.currentPeriodEndUtc;
    while (nowUtc >= endUtc) {
        startUtc = endUtc;
        endUtc = addCalendarMonthsUtc(startUtc, 1);
    }
    return { startUtc, endUtc };
}
